import { Injectable } from '@angular/core';
import { XhrFactory } from '@angular/common';
import { FetchBackend, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { addRateFinderBreadcrumb } from './rate-finder-sentry';

// FRU-25460 (Sentry ANGULAR-7FP): some Android in-app browsers wrap XMLHttpRequest.open with their own script, which calls a
// native object the page doesn't have and throws "ReferenceError: XHRInterface is not defined". The request never leaves the
// phone, so Hope's first answer was never saved. When that happens, send the same request through fetch instead.
//
// Two limits keep this narrow:
// - Hope only: the switch is on only while Hope's chat exists (on when it's created, off when it's destroyed, including a
//   fall back to the classic form). Otherwise the XHR factory builds a plain XMLHttpRequest, exactly as Angular's own
//   does, and the interceptor passes every request straight through, so the original form's requests are unchanged.
// - Only a failure proven to come before sending: the factory records an error thrown by xhr.open. Angular calls open
//   before setRequestHeader and send, so nothing reached the server and a resend can't duplicate it. A ReferenceError
//   from anywhere else (a wrapped send, for example) is passed on untouched.

// How many Hope chats exist right now. A count, not a flag, so a chat being destroyed can't switch it off under another.
let liveChats = 0;
const openFailures = new WeakSet<object>();

/** Turn the fallback on. Called when Hope's chat starts being created. */
export function enableXhrFetchFallback(): void {
  liveChats++;
}

/** Turn it off again. Called when Hope's chat is destroyed, or when it failed to be created. */
export function disableXhrFetchFallback(): void {
  if (liveChats > 0) { liveChats--; }
}

export function xhrFetchFallbackEnabled(): boolean {
  return liveChats > 0;
}

/** Tests only. */
export function resetXhrFetchFallbackForTest(): void {
  liveChats = 0;
}

/** True only for a ReferenceError that this factory saw thrown by xhr.open, before anything was sent. */
export function isBrokenXhrError(error: unknown): boolean {
  return !(error instanceof HttpErrorResponse) && error instanceof ReferenceError && openFailures.has(error);
}

/**
 * Builds the XMLHttpRequest Angular's HttpXhrBackend uses. With the fallback off it is exactly Angular's own BrowserXhr.
 * With it on, the request's open records what it throws, then throws it unchanged.
 */
@Injectable()
export class XhrFetchFallbackXhrFactory implements XhrFactory {
  build(): XMLHttpRequest {
    const xhr = new XMLHttpRequest();
    if (!xhrFetchFallbackEnabled()) {
      return xhr;
    }
    const open = xhr.open;
    (xhr as any).open = function (...args: any[]) {
      try {
        return (open as any).apply(xhr, args);
      } catch (error) {
        if (error && typeof error === 'object') { openFailures.add(error); }
        throw error;
      }
    };
    return xhr;
  }
}

@Injectable()
export class XhrFetchFallbackInterceptor implements HttpInterceptor {
  constructor(private fetchBackend: FetchBackend) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!xhrFetchFallbackEnabled()) {
      return next.handle(req);
    }
    return next.handle(req).pipe(
      catchError((error) => {
        if (!isBrokenXhrError(error) || typeof (globalThis as any).fetch !== 'function') {
          return throwError(() => error);
        }
        addRateFinderBreadcrumb('xhr_fetch_fallback', {
          url: String(req.url || '').split('?')[0].slice(0, 120),
          error: String((error as Error).message || '').slice(0, 80),
        });
        return this.fetchBackend.handle(req);
      }),
    );
  }
}
